import { toast } from 'react-toastify';
import { createPlaylist } from '../../../../../../../actions/playlist_management';
import { s3DeleteNew } from '../../../../../../../../libs/HelperContent';
import { createCategory, getSubcategories, updateCategory } from '../../../../../../../actions/resource_configuration';

/**
 * Delete a file from S3
 * @param key S3 key of the file to delete
 * @param setIsDeleting State setter for deletion status
 */
export const deleteFileFromS3 = async (key: string, setIsDeleting: (loading: boolean) => void) =>
{
	try
	{
		setIsDeleting(true);
		await s3DeleteNew(key, '');
		setIsDeleting(false);
	}
	catch (error)
	{
		console.error('Error deleting file from S3:', error);
		setIsDeleting(false);
		toast.error('Failed to delete file');
	}
};

/**
 * Submit a category or playlist based on category type
 * @param payload The payload for category/playlist creation
 * @param categoryType The type of category (category, subcategory, playlist)
 * @param setIsSubmitting State setter for submission status
 * @param onComplete Callback function on successful completion
 * @returns The API response
 */
export const submitCategory = async (
	payload: any,
	categoryType?: string,
	setIsSubmitting?: (loading: boolean) => void,
	onComplete?: () => void
) =>
{
	try
	{
		setIsSubmitting?.(true);
		console.log(`Submitting ${categoryType} with payload:`, payload);

		let response;

		// Use the appropriate API based on category type
		if (categoryType === 'playlist')
		{
			// Convert payload to match playlist API requirements
			const playlistPayload = {
				companyId     : payload.companyId,
				name          : payload.name,
				description   : payload.description,
				thumbnailUrl  : payload.thumbnailUrl,
				visibility    : payload.visibility,
				allowDownload : payload.allowDownloads,
				allowShare    : payload.allowShare,
				accessControl : payload.accessControl
			};

			response = await createPlaylist(playlistPayload);

			const refreshEvent = new CustomEvent('contentStatusChanged', {
				detail : {
					playlistId  : response?._id,
					contentType : 'playlists',
					status      : 'added'
					// categoryName : payload.name,	
					// companyId    : payload.companyId
				}
			});

			window.dispatchEvent(refreshEvent);
		}
		else
		{
			// Use existing category API for categories and subcategories
			response = await createCategory(payload);

			const successMessage = categoryType === 'category'
				? 'Category created successfully'
				: 'Subcategory created successfully';

			toast.success(successMessage);

			// Trigger a custom event to refresh the category list
			// This ensures the category list is updated without page reload
			const refreshEvent = new CustomEvent('contentStatusChanged', {
				detail : {
					categoryId  : response?._id,
					contentType : 'category',
					status      : 'added'
					// categoryName : payload.name,
					// companyId    : payload.companyId
				}
			});

			window.dispatchEvent(refreshEvent);
		}

		if (onComplete)
		{
			onComplete();
		}

		return response;
	}
	catch (error: any)
	{
		console.error(`Error creating ${categoryType}:`, error);

		// Extract backend error message from error response
		const backendMessage = error?.response?.data?.error?.message ||
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.message;

		// Display the backend error message
		toast.error(`${categoryType} ${backendMessage} `);
		throw error;
	}
	finally
	{
		setIsSubmitting?.(false);
	}
};

/**
 * Fetch subcategories for a parent category
 * @param parentCategoryId The parent category ID
 * @returns List of subcategories
 */
export const fetchSubcategories = async (parentCategoryId: string) =>
{
	try
	{
		const response:any = await getSubcategories(parentCategoryId);

		return response.data || [];
	}
	catch (error)
	{
		console.error('Error fetching subcategories:', error);
		toast.error('Failed to fetch subcategories');

		return [];
	}
};

/**
 * Update an existing category
 * @param categoryId The category ID to update
 * @param payload The updated category data
 * @param setIsSubmitting State setter for submission status
 * @returns The API response
 */
export const updateCategoryData = async (
	categoryId: string,
	payload: any,
	setIsSubmitting: (loading: boolean) => void
) =>
{
	try
	{
		setIsSubmitting(true);
		const response = await updateCategory(categoryId, payload);

		toast.success('Category updated successfully');

		// Trigger a custom event to refresh the category list
		// This ensures the category list is updated without page reload
		const refreshEvent = new CustomEvent('contentStatusChanged', {
			detail : {
				categoryId,
				contentType : 'category', // or determine from payload
				status      : 'updated'
				// categoryName : payload.name,
				// companyId    : payload.companyId
			}
		});

		window.dispatchEvent(refreshEvent);

		return response;
	}
	catch (error: any)
	{
		console.error('Error updating category:', error);

		// Extract backend error message from error response
		const backendMessage = error?.response?.data?.error?.message ||
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.message ||
			'Failed to update category. Please try again.';

		// Display the backend error message
		toast.error(backendMessage);
		throw error;
	}
	finally
	{
		setIsSubmitting(false);
	}
};